import { useEffect, useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Copy, Download, FileImage, FileText, Printer, Loader2 } from "lucide-react";
import { exportQrPng, exportQrPdf, exportQrZpl, buildZpl } from "../utils/qrExport";
import { api } from "../lib/api";

/**
 * Shared QR panel.
 *  - urls: { [modeKey]: { url, label, hint } }
 *  - defaultMode: which key to start active
 *  - resourceName: human-friendly name used in exported file names + labels
 */
export default function QrPanel({ urls, defaultMode, resourceName }) {
  const { t } = useI18n();
  const keys = Object.keys(urls);
  const [mode, setMode] = useState(defaultMode || keys[0]);
  const [exportOpen, setExportOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [copies, setCopies] = useState(1);
  const active = urls[mode];
  const src = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(active.url)}&size=200x200&margin=10`;

  const labelForFile = resourceName ? `${resourceName} (${active.label})` : active.label;

  useEffect(() => {
    // Best-effort: ignore 401/403 so the panel still works for users that
    // haven't configured printers or don't have permissions.
    api.get("/printers")
      .then((r) => setPrinters(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPrinters([]));
  }, []);

  const copy = async () => {
    await navigator.clipboard.writeText(active.url);
    toast.success(t("qr.linkCopied"));
  };

  const runExport = async (kind, fn) => {
    setBusy(kind);
    setExportOpen(false);
    try {
      await fn({ url: active.url, label: labelForFile, subLabel: active.label });
      toast.success(t("qr.exportOk").replace("{kind}", kind.toUpperCase()));
    } catch (e) {
      toast.error(t("qr.exportFailed"));
    } finally {
      setBusy(null);
    }
  };

  const printNow = async (printerId) => {
    setBusy(`print-${printerId}`);
    setPrintOpen(false);
    try {
      const zpl = buildZpl({ url: active.url, label: labelForFile, subLabel: active.label });
      const r = await api.post(`/printers/${printerId}/print`, {
        zpl,
        copies: Math.max(1, Math.min(20, Number(copies) || 1)),
      });
      toast.success(t("qr.printSent").replace("{printer}", r.data?.printer_name || ""));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("qr.printFailed"));
    } finally {
      setBusy(null);
    }
  };

  const defaultPrinter = printers.find((p) => p.is_default) || printers[0] || null;

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="qr-panel">
      {keys.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {keys.map((k) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              className={`px-3 py-2 text-xs font-mono-tech uppercase tracking-[0.1em] border border-[var(--ink-200)] ${mode === k ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`}
              data-testid={`qr-mode-${k}`}
            >
              {urls[k].label}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-5">
        <img src={src} alt="QR" width={160} height={160} className="border border-[var(--ink-200)]" />
        <div className="min-w-0 flex-1">
          <div className="label-tech">{active.hint || t("qr.scanHint")}</div>
          <div className="text-sm break-all mt-2">{active.url}</div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button className="btn-ghost" onClick={copy} data-testid="qr-copy-btn">
              <Copy size={12} /> {t("qr.copyLink")}
            </button>

            {/* Export dropdown */}
            <div className="relative">
              <button
                className="btn-ghost"
                onClick={() => { setExportOpen((v) => !v); setPrintOpen(false); }}
                disabled={!!busy}
                data-testid="qr-export-btn"
                aria-haspopup="menu"
                aria-expanded={exportOpen}
              >
                {busy && ["png", "pdf", "zpl"].includes(busy) ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                {busy && ["png", "pdf", "zpl"].includes(busy) ? t("qr.exporting") : t("qr.export")}
              </button>
              {exportOpen && (
                <div
                  className="absolute z-20 mt-2 left-0 min-w-[180px] bg-[var(--bone)] border border-[var(--ink-200)] shadow-lg"
                  role="menu"
                  data-testid="qr-export-menu"
                >
                  <button role="menuitem" className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-alt)] flex items-center gap-2 border-b border-[var(--ink-100)]" onClick={() => runExport("png", exportQrPng)} data-testid="qr-export-png">
                    <FileImage size={14} /> {t("qr.exportPng")}
                  </button>
                  <button role="menuitem" className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-alt)] flex items-center gap-2 border-b border-[var(--ink-100)]" onClick={() => runExport("pdf", exportQrPdf)} data-testid="qr-export-pdf">
                    <FileText size={14} /> {t("qr.exportPdf")}
                  </button>
                  <button role="menuitem" className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-alt)] flex items-center gap-2" onClick={() => runExport("zpl", exportQrZpl)} data-testid="qr-export-zpl">
                    <Printer size={14} /> {t("qr.exportZpl")}
                  </button>
                </div>
              )}
            </div>

            {/* Print now */}
            {defaultPrinter && (
              <div className="relative">
                <button
                  className="btn-primary"
                  onClick={() => {
                    if (printers.length === 1) {
                      printNow(printers[0].id);
                    } else {
                      setPrintOpen((v) => !v);
                      setExportOpen(false);
                    }
                  }}
                  disabled={!!busy}
                  data-testid="qr-print-now-btn"
                  aria-haspopup={printers.length > 1 ? "menu" : undefined}
                  aria-expanded={printOpen}
                >
                  {busy && busy.startsWith("print-") ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
                  {busy && busy.startsWith("print-") ? t("qr.printing") : t("qr.printNow")}
                </button>
                {printOpen && printers.length > 1 && (
                  <div className="absolute z-20 mt-2 left-0 min-w-[220px] bg-[var(--bone)] border border-[var(--ink-200)] shadow-lg" role="menu" data-testid="qr-print-menu">
                    {printers.map((p) => (
                      <button
                        key={p.id}
                        role="menuitem"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-alt)] flex items-center justify-between gap-2 border-b border-[var(--ink-100)] last:border-b-0"
                        onClick={() => printNow(p.id)}
                        data-testid={`qr-print-target-${p.id}`}
                      >
                        <span className="truncate">{p.name}</span>
                        {p.is_default && <span className="label-tech text-[var(--ink-500)]">★</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {defaultPrinter && (
              <div className="flex items-center gap-1 text-xs text-[var(--ink-500)]">
                <span className="label-tech">×</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={copies}
                  onChange={(e) => setCopies(e.target.value)}
                  className="w-14 px-2 py-1.5 border border-[var(--ink-200)] bg-[var(--surface)] text-sm font-mono-tech"
                  aria-label={t("qr.copies")}
                  data-testid="qr-copies-input"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
